import { RefObject, useCallback, useEffect, useState } from 'react';

import { useWindowResize, useWindowScroll } from '../../../hooks';

import { PopoverPosition, Position } from './Popover.types';

const OFFSET = 16;

export const usePopoverPosition = (
    triggerRef: RefObject<HTMLElement | null>,
    popoverRef: RefObject<HTMLElement | null>,
    isOpen: boolean,
    positionType: PopoverPosition,
): Position => {
    const [coordinates, setCoordinates] = useState<Position>({ top: 0, left: 0 });

    const updatePosition = useCallback(() => {
        if (!triggerRef.current) {
            return;
        }

        const rect = triggerRef.current.getBoundingClientRect();
        const popoverRect = popoverRef.current?.getBoundingClientRect();
        const popoverHeight = popoverRect?.height || 0;
        const popoverWidth = popoverRect?.width || 0;

        let top = 0;
        let left = 0;

        switch (positionType) {
            case 'top':
                top = rect.top - popoverHeight - OFFSET;
                left = rect.left + rect.width / 2 - popoverWidth / 2;
                break;
            case 'bottom':
                top = rect.bottom + OFFSET;
                left = rect.left + rect.width / 2 - popoverWidth / 2;
                break;
        }

        setCoordinates({ top, left });
    }, [positionType, triggerRef, popoverRef]);

    useWindowResize(updatePosition, 100);
    useWindowScroll(updatePosition, 0);

    useEffect(() => {
        if (isOpen && popoverRef.current) {
            updatePosition();
        }
    }, [isOpen, popoverRef, updatePosition]);

    return coordinates;
};

export const useAutoClose = (isOpen: boolean, onClose: () => void, delay: number = 2000, autoClose: boolean = true) => {
    useEffect(() => {
        if (!isOpen || !autoClose) {
            return;
        }

        const timerId = setTimeout(() => {
            onClose();
        }, delay);

        return () => {
            clearTimeout(timerId);
        };
    }, [isOpen, onClose, delay, autoClose]);
};
